import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "./App.css";
import { Toaster } from "sonner";
import Lenis from "lenis";
import { MessageCircle } from "lucide-react";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import { SiteProvider, useSite } from "./context/SiteContext";
import { AuthProvider } from "./context/AuthContext";
import { trackPageView, trackEvent } from "./lib/analytics";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import CmsAwarePage from "./components/CmsAwarePage";
import Home from "./pages/Home";
import TowerSolutions from "./pages/TowerSolutions";
import TelecomMaterials from "./pages/TelecomMaterials";
import ImportFromEurope from "./pages/ImportFromEurope";
import Projects from "./pages/Projects";
import Industries from "./pages/Industries";
import RequestQuotation from "./pages/RequestQuotation";
import About from "./pages/About";
import Contact from "./pages/Contact";
import HowItWorksPage from "./pages/HowItWorksPage";
import PartnersPage from "./pages/PartnersPage";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Disclaimer from "./pages/Disclaimer";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import DynamicCmsPage from "./pages/DynamicCmsPage";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
};

const PageViewTracker = () => {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => { if (first.current) { first.current = false; return; } trackPageView(pathname); }, [pathname]);
  return null;
};

const SmoothScroll = () => {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  return null;
};

const Builtin = ({ slug, children }) => <CmsAwarePage slug={slug}>{children}</CmsAwarePage>;

const AppShell = () => {
  const { pathname } = useLocation();
  const { settings } = useSite();
  const { lang } = useLanguage();
  const isAdminArea = pathname.startsWith("/admin") || pathname === "/login";
  const floating = settings?.floating_action || {};
  const whatsapp = String(settings?.contact?.whatsapp || "").replace(/\D/g, "");

  return <>
    {!isAdminArea && <SmoothScroll />}
    <ScrollToTop />
    <PageViewTracker />
    {!isAdminArea && <Header />}
    <main>
      <Routes>
        <Route path="/" element={<Builtin slug="home"><Home /></Builtin>} />
        <Route path="/tower-solutions" element={<Builtin slug="tower-solutions"><TowerSolutions /></Builtin>} />
        <Route path="/telecom-materials" element={<Builtin slug="telecom-materials"><TelecomMaterials /></Builtin>} />
        <Route path="/import-from-europe" element={<Builtin slug="import-from-europe"><ImportFromEurope /></Builtin>} />
        <Route path="/projects" element={<Builtin slug="projects"><Projects /></Builtin>} />
        <Route path="/industries" element={<Builtin slug="industries"><Industries /></Builtin>} />
        <Route path="/request-quotation" element={<Builtin slug="request-quotation"><RequestQuotation /></Builtin>} />
        <Route path="/about" element={<Builtin slug="about"><About /></Builtin>} />
        <Route path="/contact" element={<Builtin slug="contact"><Contact /></Builtin>} />
        <Route path="/how-it-works" element={<Builtin slug="how-it-works"><HowItWorksPage /></Builtin>} />
        <Route path="/partners" element={<Builtin slug="partners"><PartnersPage /></Builtin>} />
        <Route path="/privacy" element={<Builtin slug="privacy"><Privacy /></Builtin>} />
        <Route path="/terms" element={<Builtin slug="terms"><Terms /></Builtin>} />
        <Route path="/disclaimer" element={<Builtin slug="disclaimer"><Disclaimer /></Builtin>} />
        <Route path="/login" element={<AdminLogin />} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="*" element={<DynamicCmsPage />} />
      </Routes>
    </main>
    {!isAdminArea && <Footer />}
    {!isAdminArea && floating.enabled !== false && floating.type === "whatsapp" && whatsapp && (
      <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="send-urgent-request" onClick={() => trackEvent("whatsapp_click", { location: "floating_button" })} className="fixed bottom-6 left-6 z-40 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#25D366] text-white font-semibold shadow-lg shadow-black/40 hover:bg-[#1eb858] transition-colors duration-300">
        <MessageCircle size={20} fill="white" strokeWidth={0} />{lang === "ar" ? (floating.label_ar || "طلب عاجل") : (floating.label_en || "Send Urgent Request")}
      </a>
    )}
    <Toaster position="top-center" theme="dark" richColors />
  </>;
};

function App() {
  return <div className="App"><SiteProvider><LanguageProvider><AuthProvider><BrowserRouter><AppShell /></BrowserRouter></AuthProvider></LanguageProvider></SiteProvider></div>;
}
export default App;
